import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, filter, takeUntil } from "rxjs";
import { Functionality } from "src/app/shared/constants/functionality.constants";
import { PermissionService } from "src/app/shared/services/permission.service";
import { SessionService } from "src/app/shared/services/session.service";
import { UserService } from "src/app/shared/services/user.service";
import { IExposedInvitation, InvitationStatus } from "../../../../domain/interfaces/invitation.interface";
import { IExposedUserInOrg, IOrganizationDetails } from "../../../../domain/interfaces/organization.interface";
import { InvitationService } from "../../../../domain/services/invitation.service";
import { OrganizationService } from "../../../../domain/services/organization.service";
import { ChangeMemberRoleDialogComponent } from "../../../change-member-role-dialog/change-member-role-dialog.component";
import { RemoveMemberDialogComponent } from "../../../remove-member-dialog/remove-member-dialog.component";

const SUPER_ADMIN_ROLE = "SuperAdmin";

@Component({
  selector: "app-organization-details",
  templateUrl: "./organization-details.component.html",
  styleUrls: ["./organization-details.component.scss"],
})
export class OrganizationDetailsComponent implements OnInit, OnDestroy {
  @Input() details: IOrganizationDetails | null = null;

  /**
   * Emitted after an invitation has been successfully resent or revoked, so callers can reload
   * the organization's data (e.g. to pick up the invitation's updated expiration date).
   */
  @Output() invitationChanged = new EventEmitter<void>();

  /**
   * Emitted after a member has been successfully removed or had their role changed, so callers can
   * reload the organization's data.
   */
  @Output() memberChanged = new EventEmitter<void>();

  public readonly InvitationStatus = InvitationStatus;

  /**
   * Id of the connected user, to tell their own membership apart: a user can't remove themselves
   * or change their own role. Null until recovered.
   */
  public currentUserId: number | null = null;

  private onDestroy$ = new Subject<void>();

  constructor(
    private readonly invitationService: InvitationService,
    private readonly organizationService: OrganizationService,
    private readonly userService: UserService,
    private readonly sessionService: SessionService,
    private readonly permissionService: PermissionService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService
  ) {}

  /**
   * Recovers the connected user's id, provided they're allowed to manage members. On failure, no
   * member is told apart and the backend still refuses a change to the user's own membership.
   */
  ngOnInit(): void {
    if (!this.canRemoveMember && !this.canChangeMemberRole) {
      return;
    }

    this.userService
      .getUserInfos()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (user) => {
          this.currentUserId = user.id;
        },
        error: () => {
          this.currentUserId = null;
        },
      });
  }

  /**
   * Unsubscribes from all pending observables.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Whether the displayed organization is the one the current session is scoped to
   */
  get isCurrentOrganization(): boolean {
    return this.details?.details.id === this.sessionService.currentOrgId;
  }

  /**
   * Whether the user's role allows resending an invitation
   */
  get canResendInvitation(): boolean {
    return this.permissionService.isEnabled(Functionality.RESEND_INVITATION);
  }

  /**
   * Whether the user's role allows revoking an invitation
   */
  get canRevokeInvitation(): boolean {
    return this.permissionService.isEnabled(Functionality.REVOKE_INVITATION);
  }

  /**
   * Whether the invitations table shows its actions column: only for the current organization,
   * and only if at least one invitation action is allowed
   */
  get showInvitationActions(): boolean {
    return this.isCurrentOrganization && (this.canResendInvitation || this.canRevokeInvitation);
  }

  /**
   * Whether the user's role allows removing a member from the organization
   */
  get canRemoveMember(): boolean {
    return this.permissionService.isEnabled(Functionality.REMOVE_MEMBER);
  }

  /**
   * Whether the user's role allows changing a member's role
   */
  get canChangeMemberRole(): boolean {
    return this.permissionService.isEnabled(Functionality.CHANGE_MEMBER_ROLE);
  }

  /**
   * Whether the user can remove a SuperAdmin or change their role, i.e. is a SuperAdmin themselves
   * (only a SuperAdmin can list every organization).
   */
  get canManageSuperAdmins(): boolean {
    return this.organizationService.canGetAllOrganizations();
  }

  /**
   * Whether the members table shows its actions column: only for the current organization, and
   * only if at least one member action is allowed
   */
  get showMemberActions(): boolean {
    return this.isCurrentOrganization && (this.canRemoveMember || this.canChangeMemberRole);
  }

  /**
   * Whether the given member can be removed or have their role changed by the user: not their own
   * membership, and a SuperAdmin's only by a SuperAdmin.
   * @param member The member to check
   */
  canManageMember(member: IExposedUserInOrg): boolean {
    return member.userID !== this.currentUserId && (member.roleName !== SUPER_ADMIN_ROLE || this.canManageSuperAdmins);
  }

  /**
   * Opens the dialog to change the given member's role, notifying of the change once saved.
   * @param member The member whose role to change
   */
  openChangeRoleDialog(member: IExposedUserInOrg): void {
    this.dialog
      .open(ChangeMemberRoleDialogComponent, { width: "450px", data: { member } })
      .afterClosed()
      .pipe(filter((changed) => changed === true))
      .subscribe(() => this.memberChanged.emit());
  }

  /**
   * Opens the dialog to confirm removing the given member, notifying of the change once removed.
   * @param member The member to remove
   */
  openRemoveMemberDialog(member: IExposedUserInOrg): void {
    this.dialog
      .open(RemoveMemberDialogComponent, { width: "450px", data: { member } })
      .afterClosed()
      .pipe(filter((removed) => removed === true))
      .subscribe(() => this.memberChanged.emit());
  }

  /**
   * Resends the given pending or expired invitation, generating a new token and expiration date.
   * @param invitation The invitation to resend
   */
  resendInvitation(invitation: IExposedInvitation): void {
    this.invitationService.resendInvitation(invitation.id).subscribe({
      next: () => {
        this.snackBar.open(`✅ ${this.translateService.instant("administration.organizationDetails.invitations.resendSuccess")}`, "OK", {
          duration: 5000,
        });
        this.invitationChanged.emit();
      },
      error: () => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
      },
    });
  }

  /**
   * Revokes the given pending invitation.
   * @param invitation The invitation to revoke
   */
  revokeInvitation(invitation: IExposedInvitation): void {
    this.invitationService.revokeInvitation(invitation.id).subscribe({
      next: () => {
        invitation.status = InvitationStatus.REVOKED;
        this.snackBar.open(`✅ ${this.translateService.instant("administration.organizationDetails.invitations.revokeSuccess")}`, "OK", {
          duration: 5000,
        });
        this.invitationChanged.emit();
      },
      error: () => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });
      },
    });
  }
}
